import { useRef, useEffect, useState, useCallback } from 'react';
import { CheckIcon as Check, XIcon as X } from './ui/Icons';
import { useAppStore } from '@/store/useAppStore';
import { NumericDisplay } from 'calc-controls';
import { HelpTip } from './Tooltip';
import type { GPAVariant } from '@gpa-scales/shared-types';

const EMPTY_VARIANTS: readonly GPAVariant[] = [];

const VARIANT_HELP: Record<string, string> = {
  'Unweighted GPA':
    'All graded courses, credit-weighted on a 4.0 scale. No bonus points for advanced courses.',
  'UC Capped Weighted GPA':
    'Sophomore/Junior A-G courses only. Honors/AP/IB earn +1 each, capped at 8 points total (max 4 from 10th grade). UC eligibility threshold shown below.',
  'UC Uncapped Weighted GPA':
    'Sophomore/Junior A-G courses only. Every honors-eligible course earns +1 point with no cap.',
  'UC Unweighted GPA':
    'Sophomore/Junior A-G courses only, no bonus points. This is UC\u2019s unweighted measure.',
  'Common App Weighted GPA':
    'All graded courses. AP, IB, Honors, and Dual Enrollment each earn +1 point (5.0 scale maximum).',
};

export function GPAComparisonPanel() {
  const variants = useAppStore((s) => s.result?.variants ?? EMPTY_VARIANTS);
  const courses = useAppStore((s) => s.courses);

  // R28 — Track threshold crossings for celebration
  const prevEligibility = useRef<Record<string, boolean>>({});
  const [celebrating, setCelebrating] = useState<Set<string>>(new Set());
  const celebrateTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearCelebration = useCallback((label: string) => {
    setCelebrating((prev) => {
      const next = new Set(prev);
      next.delete(label);
      return next;
    });
  }, []);

  useEffect(() => {
    const current: Record<string, boolean> = {};
    const newlyEligible: string[] = [];

    for (const v of variants) {
      if (v.threshold !== undefined) {
        current[v.label] = v.isEligible === true;
        if (v.isEligible === true && prevEligibility.current[v.label] === false) {
          newlyEligible.push(v.label);
        }
      }
    }

    if (newlyEligible.length > 0) {
      setCelebrating((prev) => {
        const next = new Set(prev);
        for (const label of newlyEligible) {
          next.add(label);
          // Clear celebration after 1.5s
          if (celebrateTimers.current[label]) clearTimeout(celebrateTimers.current[label]);
          celebrateTimers.current[label] = setTimeout(() => clearCelebration(label), 1500);
        }
        return next;
      });
    }

    prevEligibility.current = current;
  }, [variants, clearCelebration]);

  if (courses.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface p-8 text-center">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          Add courses to see your GPA.
        </p>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Unweighted, UC Capped, UC Uncapped, UC Unweighted, and Common App variants will appear here.
        </p>
      </div>
    );
  }

  // The UC Capped GPA is the hero variant — the number a California student
  // loses sleep over. It earns its own row with a serif display treatment.
  // The other four variants render as smaller supporting siblings beneath it.
  //
  // Why five variants and not fewer? Each computes on a distinct course pool
  // (see `engine/gpaCalculations.ts`): Unweighted counts *all* courses, while
  // UC Unweighted counts only A-G 10-11 — different inputs, different numbers,
  // not duplicates. UC Capped (hero) caps bonus points at 8; UC Uncapped does
  // not. Common App Weighted runs on the all-courses pool with its own bonus
  // rule. Collapsing any two would hide a real distinction.
  const HERO_LABEL = 'UC Capped Weighted GPA';
  const hero = variants.find((v) => v.label === HERO_LABEL);
  const supporting = variants.filter((v) => v.label !== HERO_LABEL);

  const renderHero = (variant: typeof hero) => {
    if (!variant) return null;
    const isEligible = variant.isEligible;
    const hasThreshold = variant.threshold !== undefined;
    const isCelebrating = celebrating.has(variant.label);

    let gapLine: string | null = null;
    if (hasThreshold && !isEligible && variant.threshold !== undefined) {
      const gap = variant.threshold - variant.value;
      const absGap = Math.abs(gap);
      const phrase =
        absGap < 0.1
          ? 'within reach of a single grade bump'
          : absGap <= 0.25
            ? 'a focused term could close this'
            : 'a sustained turnaround is needed';
      gapLine = `${gap.toFixed(2)} below — ${phrase}`;
    }

    return (
      <div
        className={`hero-rise relative overflow-hidden rounded-xl border p-8 transition-[var(--transition-base)] sm:p-10 ${
          hasThreshold
            ? isEligible
              ? 'border-success/30 bg-gradient-to-br from-success-light to-surface'
              : 'border-error/25 bg-gradient-to-br from-error-light/70 to-surface'
            : 'border-primary/30 bg-gradient-to-br from-primary-light to-surface'
        } ${isCelebrating ? 'gpa-celebrate' : ''}`}
      >
        {/* Top accent rule — distinct from the side borders used elsewhere */}
        <span
          aria-hidden="true"
          className={`absolute inset-x-0 top-0 h-1 ${
            hasThreshold ? (isEligible ? 'bg-success' : 'bg-error') : 'bg-primary'
          }`}
        />
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-secondary)]">
              <span>UC Capped Weighted GPA</span>
              <HelpTip content={VARIANT_HELP[variant.label]} label="About UC Capped Weighted" placement="bottom" />
            </p>
            <p className="mt-3 text-xs font-normal text-[var(--color-text-muted)]">
              The number UC uses for admissions eligibility.
            </p>
          </div>
          <div className="text-left sm:text-right">
            <div
              className="gpa-hero-number text-[var(--color-text)] text-6xl sm:text-7xl"
              style={{ '--cc-font-size': '4.5rem' } as React.CSSProperties}
            >
              {variant.includedCourses === 0 ? (
                <span className="text-[var(--color-text-muted)]">N/A</span>
              ) : (
                <NumericDisplay value={variant.value} decimals={2} inline />
              )}
            </div>
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">
              {variant.includedCourses} of {variant.totalCourses} courses
            </p>
          </div>
        </div>
        {hasThreshold && (
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--color-border)] pt-4">
            <p
              className={`flex items-center gap-1.5 text-sm font-semibold ${
                isEligible ? 'text-success' : 'text-error'
              }`}
            >
              {isEligible ? (
                <>
                  <Check className="h-4 w-4" aria-hidden="true" /> Meets the {variant.threshold} UC eligibility threshold
                </>
              ) : (
                <>
                  <X className="h-4 w-4" aria-hidden="true" /> Below the {variant.threshold} UC eligibility threshold
                </>
              )}
            </p>
            {gapLine && (
              <p
                className="text-xs text-[var(--color-text-secondary)]"
                data-testid={`eligibility-gap-${variant.label}`}
              >
                {gapLine}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSupporting = (variant: typeof supporting[number], index: number) => {
    const isEligible = variant.isEligible;
    const hasThreshold = variant.threshold !== undefined;
    const isCelebrating = celebrating.has(variant.label);
    const shortLabel = variant.label
      .replace(' GPA', '')
      .replace('Weighted ', '');

    let gapLine: string | null = null;
    if (hasThreshold && !isEligible && variant.threshold !== undefined) {
      const gap = variant.threshold - variant.value;
      const absGap = Math.abs(gap);
      const phrase =
        absGap < 0.1
          ? 'within reach of a single grade bump'
          : absGap <= 0.25
            ? 'a focused term could close this'
            : 'a sustained turnaround is needed';
      gapLine = `${gap.toFixed(2)} below — ${phrase}`;
    }

    const delayClass =
      index === 0 ? 'hero-rise-delay-1' : index === 1 ? 'hero-rise-delay-2' : 'hero-rise-delay-3';

    return (
      <div
        key={variant.label}
        className={`hero-rise ${delayClass} rounded-lg border-l-[3px] bg-surface p-4 transition-[var(--transition-fast)] ${
          hasThreshold
            ? isEligible
              ? 'border-l-success'
              : 'border-l-error'
            : 'border-l-primary/60'
        } ${isCelebrating ? 'gpa-celebrate' : ''}`}
      >
        <p className="mb-1 flex items-center gap-1 text-xs font-medium text-[var(--color-text-secondary)]">
          <span>{shortLabel}</span>
          <HelpTip content={VARIANT_HELP[variant.label]} label={`About ${shortLabel}`} placement="bottom" />
        </p>
        <div
          className="text-2xl font-semibold tabular-nums text-[var(--color-text)]"
          style={{ '--cc-font-size': '1.5rem' } as React.CSSProperties}
        >
          {variant.includedCourses === 0 ? 'N/A' : <NumericDisplay value={variant.value} decimals={2} inline />}
        </div>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          {variant.includedCourses} of {variant.totalCourses} courses
        </p>
        {hasThreshold && (
          <p className={`mt-1 flex items-center gap-1 text-xs font-medium ${isEligible ? 'text-success' : 'text-error'}`}>
            {isEligible ? (
              <>
                <Check className="h-3 w-3" aria-hidden="true" /> Meets {variant.threshold} threshold
              </>
            ) : (
              <>
                <X className="h-3 w-3" aria-hidden="true" /> Below {variant.threshold} threshold
              </>
            )}
          </p>
        )}
        {gapLine && (
          <p
            className="mt-0.5 text-xs text-[var(--color-text-muted)]"
            data-testid={`eligibility-gap-${variant.label}`}
          >
            {gapLine}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {hero && renderHero(hero)}
      {supporting.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {supporting.map((variant, i) => renderSupporting(variant, i))}
        </div>
      )}
    </div>
  );
}
